/**
 * Comentarii vizibile si cautare — feature-ul (b).
 *
 * `useComentarii` e **batched din necesitate, nu din eleganta**. Grila zilei arata contorul de
 * comentarii pe FIECARE rand, iar o zi are 12-33 de randuri; matricea are pana la 300. O cerere per
 * celula ar insemna 30 de cereri la fiecare deschidere a ecranului principal si 300 la fiecare
 * schimbare de pagina in matrice. Ruta accepta `entity_ids=1,2,3` (schema `IdList`) si intoarce un
 * rand pentru FIECARE id cerut, inclusiv cele fara comentarii — asa clientul nu trebuie sa
 * deosebeasca „zero comentarii" de „nu a venit in raspuns".
 *
 * Id-urile se sorteaza si se deduplica inainte de a intra in cheie (`normalizeazaIds`): aceleasi 30
 * de entitati in alta ordine sunt acelasi raspuns, deci trebuie sa fie acelasi cache. Fara asta,
 * fiecare re-sortare a grilei ar naste o cerere noua pentru exact aceleasi date.
 *
 * `useCautare` peste `comment_fts` e feature-ul ascuns valoros: numerele de factura nu exista
 * nicaieri altundeva in fisier. „Pe ce plata era factura 260600841?" devine o interogare.
 */

import { useMemo } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api, type CuImplicite } from '../lib/api.ts';
import { chei, normalizeazaIds } from '../lib/query-keys.ts';
import type {
  CommentBatchQuery,
  CommentDto,
  CommentSummaryDto,
  CreateCommentInput,
  PatchCommentInput,
} from '../../../../functions/api/schemas.ts';

export type { CommentDto, CommentSummaryDto };

export type TipEntitateComentariu = CommentBatchQuery['entity_type'];
export type FelComentariu = NonNullable<CommentBatchQuery['kind']>;

// ---------------------------------------------------------------------------
// GET /api/comments — contoare batched
// ---------------------------------------------------------------------------

export interface RaspunsContoareComentarii {
  entityType: string;
  summaries: CommentSummaryDto[];
}

/**
 * UNA singura cerere pentru N entitati.
 *
 * Extrasa din hook ca sa poata fi testata direct: forma cererii (`entity_ids=1,2,3`) E contractul cu
 * ruta, iar o regresie aici nu s-ar vedea ca eroare — s-ar vedea ca 30 de cereri in loc de una.
 */
export function fnComentariiLot(
  entityType: TipEntitateComentariu,
  entityIds: readonly number[],
  optiuni?: { kind?: FelComentariu; semnal?: AbortSignal },
): Promise<RaspunsContoareComentarii> {
  return api.get<RaspunsContoareComentarii>('/comments', {
    interogare: {
      entity_type: entityType,
      // `construiesteInterogare` uneste lista cu virgula: exact forma pe care o cere `IdList`.
      entity_ids: normalizeazaIds(entityIds),
      kind: optiuni?.kind,
    },
    semnal: optiuni?.semnal,
  });
}

/**
 * Contoarele pentru N entitati, intr-o SINGURA cerere.
 *
 * `entityIds` gol => nu se cere nimic. Ruta ar raspunde cu 400 („trimite `entity_ids=1,2,3`"), iar
 * o eroare rosie in grila pentru un tabel inca neincarcat ar fi zgomot pur.
 */
export function useComentarii(
  entityType: TipEntitateComentariu,
  entityIds: readonly number[],
  optiuni?: { kind?: FelComentariu; activ?: boolean },
): UseQueryResult<RaspunsContoareComentarii, Error> {
  const ids = useMemo(() => normalizeazaIds(entityIds), [entityIds]);

  return useQuery({
    queryKey: chei.comentarii.lot(entityType, ids, optiuni?.kind),
    queryFn: ({ signal }) =>
      fnComentariiLot(entityType, ids, { kind: optiuni?.kind, semnal: signal }),
    enabled: optiuni?.activ !== false && ids.length > 0,
    // Contoarele insotesc o grila care se redeseneaza des; pastreaza-le pe cele vechi la paginare.
    placeholderData: keepPreviousData,
  });
}

/** Index pe `entityId`, ca grila sa nu faca un `find` per rand desenat. */
export function indexeazaContoare(
  raspuns: RaspunsContoareComentarii | undefined,
): ReadonlyMap<number, CommentSummaryDto> {
  return new Map((raspuns?.summaries ?? []).map((s) => [s.entityId, s]));
}

// ---------------------------------------------------------------------------
// GET /api/comments?full=1 — firul complet al unei entitati
// ---------------------------------------------------------------------------

export interface RaspunsFirComentarii {
  entityType: string;
  entityId: number;
  comments: CommentDto[];
}

export function useFirComentarii(
  entityType: TipEntitateComentariu,
  entityId: number | null | undefined,
  optiuni?: { kind?: FelComentariu },
): UseQueryResult<RaspunsFirComentarii, Error> {
  return useQuery({
    queryKey: chei.comentarii.fir(entityType, entityId ?? 0, optiuni?.kind),
    queryFn: ({ signal }) =>
      api.get<RaspunsFirComentarii>('/comments', {
        interogare: {
          entity_type: entityType,
          entity_id: entityId ?? 0,
          full: '1',
          kind: optiuni?.kind,
        },
        semnal: signal,
      }),
    enabled: typeof entityId === 'number' && entityId > 0,
  });
}

// ---------------------------------------------------------------------------
// POST /api/comments
// ---------------------------------------------------------------------------

export type IntrareComentariu = CuImplicite<CreateCommentInput, 'kind'>;

export function fnPostComentariu(intrare: IntrareComentariu): Promise<CommentDto> {
  return api.post<CommentDto>('/comments', intrare);
}

/**
 * Un comentariu nou schimba trei lucruri: firul, contorul din grila si — cand e `invoice_ref` —
 * coloana `Facturi` a randului, pentru ca serverul extrage automat numerele de factura din text.
 * De aceea se invalideaza si ziua, nu doar prefixul de comentarii.
 */
export function usePostComentariu(): UseMutationResult<CommentDto, Error, IntrareComentariu> {
  const queryClient = useQueryClient();
  return useMutation<CommentDto, Error, IntrareComentariu>({
    mutationKey: chei.mutatii.postComentariu(),
    mutationFn: fnPostComentariu,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chei.comentarii.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.zi.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.matrice.tot() });
    },
  });
}

// ---------------------------------------------------------------------------
// PATCH /api/comments/:id
// ---------------------------------------------------------------------------

export interface VariabilePatchComentariu {
  id: number;
  patch: PatchCommentInput;
}

export type RaspunsPatchComentariu = CommentDto | { id: number; deleted: true };

export function fnPatchComentariu(
  variabile: VariabilePatchComentariu,
): Promise<RaspunsPatchComentariu> {
  return api.patch<RaspunsPatchComentariu>(`/comments/${variabile.id}`, variabile.patch);
}

export function usePatchComentariu(): UseMutationResult<
  RaspunsPatchComentariu,
  Error,
  VariabilePatchComentariu
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsPatchComentariu, Error, VariabilePatchComentariu>({
    mutationKey: chei.mutatii.patchComentariu(),
    mutationFn: fnPatchComentariu,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chei.comentarii.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.zi.tot() });
    },
  });
}

// ---------------------------------------------------------------------------
// POST /api/comments/:id/resolve
// ---------------------------------------------------------------------------

export interface RaspunsRezolvare {
  id: number;
  resolved: boolean;
  resolvedAt: number | null;
}

export interface VariabileRezolvare {
  id: number;
  /** Implicit `true` pe server; `false` redeschide firul. */
  resolved?: boolean;
}

export function fnRezolvaComentariu(variabile: VariabileRezolvare): Promise<RaspunsRezolvare> {
  return api.post<RaspunsRezolvare>(`/comments/${variabile.id}/resolve`, {
    resolved: variabile.resolved ?? true,
  });
}

export function useRezolvaComentariu(): UseMutationResult<
  RaspunsRezolvare,
  Error,
  VariabileRezolvare
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsRezolvare, Error, VariabileRezolvare>({
    mutationKey: chei.mutatii.rezolvaComentariu(),
    mutationFn: fnRezolvaComentariu,
    onSuccess: () => {
      // `unresolvedCount` din contoare si punctul rosu din grila depind de asta.
      void queryClient.invalidateQueries({ queryKey: chei.comentarii.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.zi.tot() });
    },
  });
}

// ---------------------------------------------------------------------------
// GET /api/search
// ---------------------------------------------------------------------------

export interface RezultatComentariu {
  id: number;
  entityType: string;
  entityId: number | null;
  kind: string;
  authorDisplay: string;
  createdAt: number;
  /** Fragment cu termenul incadrat in `[...]`, direct din `snippet()` FTS5. */
  snippet: string;
  body: string;
  rank: number;
}

export interface RezultatFactura {
  id: number;
  raw: string;
  normalized: string;
  docKind: string | null;
  entryId: number | null;
  fxTxnId: number | null;
  businessDate: string | null;
  runLabel: string | null;
  supplierName: string | null;
  amountRonMinor: number | null;
  status: string | null;
}

export interface RaspunsCautare {
  query: string;
  ftsQuery: string;
  comments: RezultatComentariu[];
  /** Raspunsul la „pe ce plata era factura X": zi, furnizor, suma, stare. */
  invoiceRefs: RezultatFactura[];
}

/** Ruta cere minimum 2 caractere; sub prag nu se trimite nimic si nu se arata nicio eroare. */
export const LUNGIME_MINIMA_CAUTARE = 2;

export function useCautare(
  q: string,
  optiuni?: { limit?: number; entityType?: TipEntitateComentariu; activ?: boolean },
): UseQueryResult<RaspunsCautare, Error> {
  const termen = q.trim();
  return useQuery({
    queryKey: chei.comentarii.cautare(termen, {
      limit: optiuni?.limit,
      entityType: optiuni?.entityType,
    }),
    queryFn: ({ signal }) =>
      api.get<RaspunsCautare>('/search', {
        interogare: { q: termen, limit: optiuni?.limit, entity_type: optiuni?.entityType },
        semnal: signal,
      }),
    enabled: optiuni?.activ !== false && termen.length >= LUNGIME_MINIMA_CAUTARE,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
